import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowRight, CheckCircle2, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * /find-pass — magic-link request screen.
 *
 * Always shows the same "check your email" success state on submit
 * (regardless of whether the email exists) to prevent enumeration.
 */
export default function FindPass() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/pass-recovery/request`, { email: email.trim() });
    } catch {
      // Even on network error, show the same success state — the backend
      // is the only source of truth and it never reveals existence.
    } finally {
      setSubmitting(false);
      setSubmitted(true);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-[#EAE7E1]" data-testid="find-pass-page">
      <header className="border-b border-[#1A1A1A]">
        <div className="max-w-[520px] mx-auto px-5 sm:px-6 py-5 flex items-center justify-between">
          <Link to="/" className="text-sm tracking-[0.4em] font-bold text-[#D4AF37] uppercase" data-testid="find-pass-home-link">VelvetGate</Link>
          <Link to="/" className="text-xs text-[#777] hover:text-[#EAE7E1] transition-colors">← Home</Link>
        </div>
      </header>

      <main className="max-w-[520px] mx-auto px-5 sm:px-6 py-10 sm:py-16">
        <div className="text-[10px] tracking-[0.35em] text-[#D4AF37] uppercase font-semibold mb-3" data-testid="find-pass-eyebrow">
          Pass Recovery
        </div>
        <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-[#F3EEE7] mb-3" data-testid="find-pass-title">
          Find My Pass
        </h1>
        <p className="text-sm sm:text-base text-[#B9B1A6] leading-relaxed mb-8" data-testid="find-pass-description">
          Enter the email you used during registration. If we find a pass on file, we&rsquo;ll send you a secure link to view it.
        </p>

        {!submitted ? (
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="find-pass-form">
            <div>
              <label htmlFor="recovery-email" className="text-xs tracking-[0.15em] uppercase text-[#777] font-semibold">
                Email
              </label>
              <div className="relative mt-1.5">
                <Mail className="w-4 h-4 text-[#555] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  id="recovery-email"
                  type="email"
                  required
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full pl-10 pr-3 py-3 rounded-sm bg-[#0A0A0A] border border-[#1A1A1A] text-[#EAE7E1] placeholder-[#555] focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] focus:outline-none"
                  data-testid="find-pass-email-input"
                />
              </div>
              <p className="text-[11px] text-[#555] mt-1.5">
                We&rsquo;ll send a recovery link that&rsquo;s valid for 30 minutes.
              </p>
            </div>

            <button
              type="submit"
              disabled={submitting || !email.trim()}
              className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#8C6B1F] via-[#D4AF37] to-[#F2D27A] hover:opacity-90 disabled:opacity-50 text-[#050505] px-6 py-3.5 rounded-sm font-bold text-sm transition-opacity"
              data-testid="find-pass-submit-button"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Sending&hellip;</>
              ) : (
                <>Send Recovery Link <ArrowRight className="w-4 h-4" /></>
              )}
            </button>
          </form>
        ) : (
          <div
            className="border border-[#D4AF37]/25 rounded-sm bg-gradient-to-br from-[#0F0D08] to-[#050505] p-6"
            data-testid="find-pass-success-state"
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="w-5 h-5 text-emerald-400" />
              </div>
              <div className="min-w-0">
                <div className="text-lg font-bold text-[#F3EEE7] mb-1" data-testid="find-pass-success-heading">
                  Check your email
                </div>
                <p className="text-sm text-[#B9B1A6] leading-relaxed">
                  If a pass is on file for <span className="font-mono text-[#EAE7E1] break-all">{email}</span>, a recovery link has been sent. The link expires in 30 minutes.
                </p>
                <p className="text-xs text-[#777] mt-3">
                  Didn&rsquo;t receive anything? Check your spam folder, or{' '}
                  <button
                    type="button"
                    onClick={() => { setSubmitted(false); }}
                    className="text-[#D4AF37] hover:underline"
                    data-testid="find-pass-try-again"
                  >
                    try a different email
                  </button>
                  .
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="mt-10 pt-6 border-t border-[#1A1A1A] flex flex-wrap gap-x-5 gap-y-2 text-xs text-[#777]">
          <Link to="/claim"   className="hover:text-[#D4AF37] transition-colors" data-testid="find-pass-footer-claim">Have a claim code?</Link>
          <Link to="/faq"     className="hover:text-[#D4AF37] transition-colors" data-testid="find-pass-footer-faq">FAQ</Link>
          <Link to="/contact" className="hover:text-[#D4AF37] transition-colors" data-testid="find-pass-footer-contact">Contact</Link>
          <span className="ml-auto text-[#444]">© VelvetGate</span>
        </div>
      </main>
    </div>
  );
}
